/**
 * Check if a feature is allowed by DRM policy
 * @param {Object} drmPolicy - The DRM policy object from API
 * @param {string} featureName - Feature to check: 'bookmarks', 'highlights', 'notes', 'freehand_drawing', 'search', 'comments', 'ai_features', 'translation', 'download', 'printing', 'citations', 'pullout'
 * @returns {boolean} - Whether the feature is allowed
 */
export const isFeatureAllowed = (drmPolicy, featureName) => {
  if (!drmPolicy) return true; // Default to allowed if no policy

  switch (featureName) {
    case "bookmarks":
      return drmPolicy.bookmarks?.allowed === true;

    case "highlights":
      return drmPolicy.annotations?.highlights?.allowed === true;

    case "underline":
      return drmPolicy.annotations?.underline?.allowed === true;

    case "notes":
      return drmPolicy.annotations?.notes?.allowed === true;

    case "freehand_drawing":
      return drmPolicy.annotations?.freehand_drawing?.allowed === true;

    case "textbox":
      return drmPolicy.annotations?.textbox?.allowed === true;

    case "search":
      return drmPolicy.search?.allowed === true;

    case "comments":
      return drmPolicy.comments?.allowed === true;

    case "ai_features":
      return drmPolicy.ai_features?.allowed === true;

    case "translation":
      return drmPolicy.translation?.allowed === true;

    case "download":
      return drmPolicy.downloads?.allowed === true;

    case "printing":
      return drmPolicy.printing?.allowed === true;

    case "citations":
      return drmPolicy.citations?.allowed === true;

    // Client DRM key is singular: drm_policy.pullout.allowed
    case "pullout":
      return drmPolicy.pullout?.allowed === true;

    default:
      return true;
  }
};

/** Default max characters allowed when copying selected text (clipboard). */
const DEFAULT_COPY_CHAR_LIMIT = 500;

/**
 * Whether clipboard copy is allowed by DRM policy.
 * @param {Object} drmPolicy - The DRM policy object from API
 * @returns {boolean} - True if copy is allowed (default true when no policy)
 */
export const isCopyAllowed = (drmPolicy) => {
  if (!drmPolicy) return true;
  const allowed = drmPolicy.clipboard_copy?.allowed;
  if (allowed === false) return false;
  return true;
};

/**
 * Get the max characters allowed when copying selected text.
 * Reads from drm_policy.clipboard_copy.max_characters.
 * When clipboard_copy exists but max_characters is absent, unlimited copy is allowed.
 * @param {Object} drmPolicy - The DRM policy object from API
 * @returns {number} - Max characters allowed for copy (default 500), or -1 for unlimited
 */
export const getCopyCharLimit = (drmPolicy) => {
  const clipboardCopy = drmPolicy?.clipboard_copy;
  if (!clipboardCopy) return DEFAULT_COPY_CHAR_LIMIT;
  const raw = clipboardCopy.max_characters;
  if (raw == null) return -1; // key absent → unlimited
  const limit = Number(raw);
  if (limit === -1) return -1;
  if (!isNaN(limit) && limit > 0) return limit;
  return DEFAULT_COPY_CHAR_LIMIT;
};

/**
 * Truncate text to maxLength, preferring a word boundary when reasonable.
 * A maxLength of -1 means unlimited (no truncation).
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Max length, or -1 for unlimited
 * @returns {string} - Truncated text
 */
export const truncateForCopy = (text, maxLength) => {
  if (maxLength === -1) return text;
  if (!text || text.length <= maxLength) return text;
  const truncated = text.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(" ");
  if (lastSpace > maxLength * 0.5) {
    return truncated.substring(0, lastSpace);
  }
  return truncated;
};

/**
 * DRM viewing_rules.first_page display mode.
 * Distinct from metadata.first_page (roman/arabic page label offset).
 * @param {Object} drmPolicy - The DRM policy object from API
 * @returns {"cover"|"default"}
 */
export const getDrmFirstPageDisplayMode = (drmPolicy) => {
  const raw = drmPolicy?.viewing_rules?.first_page;
  if (typeof raw !== "string") return "default";
  return raw.trim().toLowerCase() === "cover" ? "cover" : "default";
};

/**
 * Whether double-page / two-up should isolate the cover (first page alone).
 * Only true when DRM viewing_rules.first_page is "cover".
 * @param {Object} drmPolicy - The DRM policy object from API
 * @returns {boolean}
 */
export const shouldIsolateCoverInDoublePage = (drmPolicy) =>
  getDrmFirstPageDisplayMode(drmPolicy) === "cover";

/**
 * Check if a page number is within the viewable page ranges
 * @param {Object} drmPolicy - The DRM policy object from API
 * @param {number} pageNumber - Page number to check (1-indexed)
 * @returns {boolean} - Whether the page is viewable
 */
export const isPageInViewableRange = (drmPolicy, pageNumber) => {
  if (!drmPolicy || !drmPolicy.viewing_rules) return true; // No restrictions

  const ranges = drmPolicy.viewing_rules.page_ranges;
  if (!ranges || !Array.isArray(ranges) || ranges.length === 0) return true;

  return ranges.some(
    (range) => pageNumber >= range.start && pageNumber <= range.end,
  );
};

/**
 * Get the overall min and max viewable pages from DRM policy viewing_rules.
 * @param {Object} drmPolicy - The DRM policy object from API
 * @returns {Object|null} { minPage, maxPage } or null if no restrictions
 */
export const getViewablePageRange = (drmPolicy) => {
  if (!drmPolicy || !drmPolicy.viewing_rules) return null;

  const ranges = drmPolicy.viewing_rules.page_ranges;
  if (!ranges || !Array.isArray(ranges) || ranges.length === 0) return null;

  const minPage = Math.min(...ranges.map((r) => r.start));
  const maxPage = Math.max(...ranges.map((r) => r.end));

  return { minPage, maxPage };
};

/**
 * Get the effective start page considering viewing_rules and the requested start page.
 * - If no viewing rules, returns requestedStartPage or 1.
 * - If requestedStartPage is within the viewing range, returns it.
 * - Otherwise returns the first allowed page from viewing_rules.
 * @param {Object} drmPolicy
 * @param {number|null} requestedStartPage - The start_page from initial_view
 * @returns {number}
 */
export const getEffectiveStartPage = (drmPolicy, requestedStartPage) => {
  const range = getViewablePageRange(drmPolicy);

  if (!range) {
    // No viewing restrictions – use requested start page or default to 1
    return requestedStartPage && requestedStartPage > 0
      ? requestedStartPage
      : 1;
  }

  // If requested start page is valid and within viewing range, use it
  if (
    requestedStartPage &&
    requestedStartPage > 0 &&
    isPageInViewableRange(drmPolicy, requestedStartPage)
  ) {
    return requestedStartPage;
  }

  // Default to first page of viewing range
  return range.minPage;
};

/**
 * Check if a chunk (by index) contains any pages within the viewable range.
 * Returns true if no restrictions or if any page in the chunk is viewable.
 * @param {Object} drmPolicy
 * @param {number} chunkIndex - 0-based chunk index
 * @param {number} totalPages - Total pages in the document
 * @param {number} chunkSize - Pages per chunk (default 5)
 * @returns {boolean}
 */
export const isChunkWithinViewableRange = (
  drmPolicy,
  chunkIndex,
  totalPages,
  chunkSize = 5,
) => {
  if (!drmPolicy || !drmPolicy.viewing_rules) return true; // No restrictions

  const range = getViewablePageRange(drmPolicy);
  if (!range) return true;

  const chunkStart = chunkIndex * chunkSize + 1;
  const chunkEnd = Math.min((chunkIndex + 1) * chunkSize, totalPages);

  // Allow chunk if its page range overlaps with the viewable range at all.
  // The per-page rendering filter (effectivePageNumbers) handles the rest.
  return chunkEnd >= range.minPage && chunkStart <= range.maxPage;
};
